import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular-ivy';
import { NotificationsService } from 'app/services/notifications.service';
import { IAppointment } from 'shared/interfaces/appointment.interface';
import { IPromptuary } from 'shared/interfaces/promptuary.interface';
import { PromptuaryService } from 'shared/services/promptuary/promptuary.service';

import { SentryService } from './../../../core/sentry/sentry.service';
import {
	PromptuaryBreadcrumb,
	PromptuaryErrors,
	SentryPromptuaryErrorsMessages,
} from './../../interfaces/promptuary.interface';
import { PromptuarySidebarService } from '../../services/sidebar/promptuary-sidebar.service';
import { CustomPromptuaryValidatorsService } from './custom-promptuary-validators.service';

@Injectable({
	providedIn: 'root',
})
//Jogar funções e variaveis compartilhadas aqui, pra facilitar depois unirmos os dois prontuarios
export class UnifiedPromptuaryService {
	constructor(
		public fb: FormBuilder,
		public validators: CustomPromptuaryValidatorsService,
		public promptuaryService: PromptuaryService,
		public notificationService: NotificationsService,
		public promptuarySidebarService: PromptuarySidebarService,
		public translateService: TranslateService,
		public sentryService: SentryService
	) {}
	promptuaryMode: 'manage' | 'view';
	form: FormGroup;
	autosaveLoading: string[] = [];
	isLoading = true;
	isRegisterModalOpen = false;
	isRegistering = false;
	isPromptuaryCreated = false;
	serviceEvolutionQuality = 'default';
	wasSpeechToTextUsed = false;
	appointmentId: string;

	createPromptuary(customerId, eventId, showAlertOnError = true) {
		//Deve ser chamado antes da inicialização do prontuário
		this.setBreadcrumb(PromptuaryBreadcrumb.CREATING_PROMPTUARY);
		this.promptuaryService
			.createPromptuary({
				customerId,
				eventId,
			})
			.subscribe(
				() => {
					this.setBreadcrumb(PromptuaryBreadcrumb.PROMPTUARY_CREATED, {
						customerId,
						eventId,
					});
					this.isPromptuaryCreated = true;
					this.isLoading = false;
					this.promptuaryMode = 'manage';
				},
				({ error }) => {
					if (error?.statusCode === 409) {
						this.capturePromptuaryError(
							PromptuaryErrors.PROMPTUARY_ALREADY_EXISTS,
							SentryPromptuaryErrorsMessages.PROMPTUARY_ALREADY_EXISTS,
							false
						);
						this.promptuaryMode = 'manage';
						this.isPromptuaryCreated = true;
						this.isLoading = false;
						return;
					}
					this.capturePromptuaryError(
						PromptuaryErrors.CREATE_PROMPTUARY_ERROR,
						SentryPromptuaryErrorsMessages.CREATE_PROMPTUARY_ERROR,
						showAlertOnError
					);
					this.resetPromptuary();
					this.closePromptuary();
				}
			);
	}

	closePromptuary() {
		this.promptuarySidebarService.toggleSidebar(false);
	}

	setAutoSaveLoading(property) {
		this.form.updateValueAndValidity();
		this.autosaveLoading = [...this.autosaveLoading, property];
		this.setBreadcrumb('starting auto save loading', {
			property,
			loadingArray: this.autosaveLoading,
		});
	}

	autosaveComplete(property: string): void {
		this.autosaveLoading = this.autosaveLoading.filter(
			(elem) => elem != property
		);
		this.setBreadcrumb('auto save loading complete', {
			property,
			loadingArray: this.autosaveLoading,
		});
	}

	setFormModel(
		promptuary: IPromptuary,
		appointment: IAppointment,
		type: 'default' | 'crm' = 'default'
	) {
		this.form = this.fb.group({
			task: this.fb.group({
				description: [null, this.validators.requiredWhenHasLinks()],
				links: this.fb.array([]),
				id: [null],
			}),
			serviceEvolution: [
				promptuary?.serviceEvolution ?? null,
				[
					type === 'default' ? Validators.required : Validators.nullValidator,
					type === 'default'
						? Validators.minLength(10)
						: Validators.nullValidator,
				],
			],
			serviceEvolutionPsychiatrist: this.fb.group({
				currentDiseaseHistory: [
					promptuary?.serviceEvolutionPsychiatrist?.currentDiseaseHistory ??
						null,
					type === 'crm' ? Validators.required : Validators.nullValidator,
				],
				familyBackground: [
					promptuary?.serviceEvolutionPsychiatrist?.familyBackground ?? null,
					type === 'crm' ? Validators.required : Validators.nullValidator,
				],
				habitsAndVices: [
					promptuary?.serviceEvolutionPsychiatrist?.habitsAndVices ?? null,
					type === 'crm' ? Validators.required : Validators.nullValidator,
				],
				medicationsAndDoses: [
					promptuary?.serviceEvolutionPsychiatrist?.medicationsAndDoses ?? null,
					type === 'crm' ? Validators.required : Validators.nullValidator,
				],
				personalBackground: [
					promptuary?.serviceEvolutionPsychiatrist?.personalBackground ?? null,
					type === 'crm' ? Validators.required : Validators.nullValidator,
				],
			}),
			// The questions from CGI are added to the array asynchronously in promptuary-cgi component
			cgi: new FormArray([], Validators.required),
			reasons: [appointment?.contactReason ?? null, Validators.required],
			outcome: [promptuary?.outcome ?? null, Validators.required],
			outcomeForwardJourney: [
				promptuary?.outcomeForwardJourney ?? null,
				this.validators.requiredWhenOutcomeIsEqualTo(
					'outcomeForwardJourney',
					'Encaminhamento de plano de cuidados'
				),
			],
			outcomeForwardJourneySessionNumber: [
				promptuary?.outcomeForwardJourneySessionNumber ?? null,
				this.validators.requiredWhenOutcomeIsEqualTo(
					'outcomeForwardJourneySessionNumber',
					'Encaminhamento de plano de cuidados'
				),
			],
			outcomeSpecialty: [
				promptuary?.outcomeSpecialty ?? null,
				this.validators.requiredWhenOutcomeIsEqualTo(
					'outcomeSpecialty',
					'Encaminhamento especialista'
				),
			],
			outcomeOtherSpecialty: [
				promptuary?.outcomeOtherSpecialty ?? null,
				Validators.nullValidator,
			],
			complaintAndDuration: [
				promptuary?.complaintAndDuration ?? null,
				type === 'crm' ? Validators.required : Validators.nullValidator,
			],
			psychicExamination: [
				promptuary?.psychicExamination ?? null,
				type === 'crm' ? Validators.required : Validators.nullValidator,
			],
			clinicalExamination: [
				promptuary?.clinicalExamination ?? null,
				type === 'crm' ? Validators.required : Validators.nullValidator,
			],
			conduct: [
				promptuary?.conduct ?? null,
				type === 'crm' ? Validators.required : Validators.nullValidator,
			],
			alarmSignals: [
				promptuary?.alarmSignals ?? null,
				type === 'crm' ? Validators.required : Validators.nullValidator,
			],
			alarmSignalsReason: [
				promptuary?.alarmSignalsReason ?? null,
				promptuary?.alarmSignals
					? Validators.required
					: Validators.nullValidator,
			],
		});

		this.setBreadcrumb('form model set', {
			type,
		});

		this.form.get('alarmSignals')?.valueChanges.subscribe((value: boolean) => {
			const alarmSignalsReasonControl = this.form.get('alarmSignalsReason');
			alarmSignalsReasonControl?.setValidators(
				this.validators.alarmSignalsReasonValidator(value)
			);
			alarmSignalsReasonControl?.updateValueAndValidity();
		});
	}

	toggleRegisterModal(value) {
		if (value) {
			this.toggleRegistering(true);
			this.canRegister().subscribe(
				(res) => {
					res.canRegister
						? (this.isRegisterModalOpen = value)
						: this.notificationService.showError(
								res.reason.description,
								res.reason.title
						  );
					this.toggleRegistering(false);
				},
				(e) => {
					this.toggleRegistering(false);
					this.notificationService.showError(e);
				}
			);
			return;
		}
		this.isRegisterModalOpen = value;
	}

	toggleRegistering(value) {
		this.isRegistering = value;
	}

	resetPromptuary() {
		this.isPromptuaryCreated = false;
		this.isRegistering = false;
		this.isLoading = true;
		this.autosaveLoading = [];
		this.serviceEvolutionQuality = 'default';
		this.wasSpeechToTextUsed = false;
		this.form?.reset();
		this.setBreadcrumb('promptuary form reseted');
	}

	canRegister() {
		return this.promptuaryService.canRegister(this.appointmentId);
	}

	cgiOnRequired(required: boolean) {
		this.form
			.get('cgi')
			.setValidators(required ? Validators.required : Validators.nullValidator);
		this.form.get('cgi').updateValueAndValidity();
	}

	setBreadcrumb(message, property?) {
		Sentry.addBreadcrumb({
			category: 'ui.promptuary',
			message: message,
			data: property ?? {},
			level: 'info',
			type: 'default',
		});
	}

	capturePromptuaryError(
		error: PromptuaryErrors,
		sentryMessage: SentryPromptuaryErrorsMessages,
		showAlert = true
	) {
		if (showAlert) {
			this.notificationService.showError(this.translateService.instant(error));
		}
		this.sentryService.captureException(new Error(sentryMessage));
	}
}
